import type { RegistryEncounter, RegistryRecord } from '../../../api/registryRecordApi';
import { PHASE_STATUS_META, type PhaseStatus } from '../../../components/registry/RegistryPhaseWorkspace';

export { PHASE_STATUS_META };
export type { PhaseStatus };

/**
 * Fazele fluxului medical din proiectul HEPASKIN pentru pacienții hepato importați:
 * 1. Conștientizare & Anamneză · 2. Screening & Diagnostic ·
 * 3. Tratament & Chirurgie · 4. Registru & Follow-up.
 */

export type HepatoPhase = 1 | 2 | 3 | 4;

export interface PhaseDef {
  phase: HepatoPhase;
  title: string;
}

export const HEPATO_PHASES: PhaseDef[] = [
  { phase: 1, title: 'Conștientizare & Anamneză' },
  { phase: 2, title: 'Screening & Diagnostic' },
  { phase: 3, title: 'Tratament & Chirurgie' },
  { phase: 4, title: 'Registru & Follow-up' },
];

/** Analizele AFP din lista de laborator (când tabela dedicată `afp[]` e goală). */
export const isAfpLab = (lab: { testCode: string | null; testName: string }): boolean =>
  /afp/i.test(lab.testCode ?? '') || /afp|fetoprotein/i.test(lab.testName);

/** Documentele de imagistică (kind setat de import: `imagistica` → IMAGING_REPORT). */
export const imagingDocuments = (record: RegistryRecord) =>
  record.documents.filter((d) => d.kind === 'IMAGING_REPORT');

/** Documentul chestionar (kind: `chestionar` → QUESTIONNAIRE). */
export const questionnaireDocument = (record: RegistryRecord) =>
  record.documents.find((d) => d.kind === 'QUESTIONNAIRE') ?? null;

export const isHospitalEncounter = (e: RegistryEncounter) =>
  e.type === 'HOSPITALIZATION' || e.type === 'OBSERVATION';

export const hasQuestionnaireContent = (record: RegistryRecord): boolean => {
  const q = record.questionnaire;
  if (!q) return false;
  return Object.values(q).some(
    (s) => s && typeof s === 'object' && !Array.isArray(s) && Object.keys(s as object).length > 0,
  );
};

/** Statusul celor 4 faze, derivat din fișa de registru importată. */
export function computePhaseStatuses(record: RegistryRecord): Record<HepatoPhase, PhaseStatus> {
  // Faza 1 — chestionarul
  const faza1: PhaseStatus = hasQuestionnaireContent(record) ? 'COMPLETE' : 'MISSING';

  // Faza 2 — AFP + (imagistică sau HP) = completă; orice bucată = parțială
  const afpPresent = (record.afp?.length ?? 0) > 0 || (record.labs ?? []).some(isAfpLab);
  const imaging = imagingDocuments(record).length > 0;
  const hp = (record.histopathologies?.length ?? 0) > 0;
  const anyLabs = (record.labs?.length ?? 0) > 0;
  const faza2: PhaseStatus =
    afpPresent && (imaging || hp)
      ? 'COMPLETE'
      : afpPresent || imaging || hp || anyLabs
        ? 'PARTIAL'
        : 'MISSING';

  // Faza 3 — tratament sau internare cu proceduri = completă; internare fără proceduri = parțială
  const hasTreatments = (record.treatments?.length ?? 0) > 0;
  const hospitalizations = record.encounters.filter(isHospitalEncounter);
  const withProcedures = hospitalizations.some((e) => (e.procedures?.length ?? 0) > 0);
  const faza3: PhaseStatus =
    hasTreatments || withProcedures ? 'COMPLETE' : hospitalizations.length > 0 ? 'PARTIAL' : 'MISSING';

  // Faza 4 — caz cu diagnostic derivat = completă; fișă fără diagnostic = parțială
  const faza4: PhaseStatus = record.cases.some((c) => c.diagnosis) ? 'COMPLETE' : 'PARTIAL';

  return { 1: faza1, 2: faza2, 3: faza3, 4: faza4 };
}
